import { useMemo } from 'react';

import { formatDurationFromSeconds } from '../../utils/datetime';
import { ReactComponent as CompleteIcon } from '../../icons/complete.svg';
import { AgeLimitLabel } from '../AgeLimitLabel/AgeLimitLabel';
import { Thumbnail } from '../Thumbnail/Thumbnail';

import { AdBadge } from './shared/AdBadge/AdBadge';
import { SimpleLiveBadge } from './shared/SimpleLiveBadge/SimpleLiveBadge';

import type { ImageSources } from '../../helpers/images';
import type { ThumbnailProps } from '../Thumbnail/Thumbnail';
import type { ReactNode, VFC } from 'react';

interface AssetThumbnailProps {
  imageSources?: ImageSources;
  className?: string;
  badge?: ReactNode;
  secondaryBadge?: ReactNode;
  isComplete?: boolean;
  isSponsored?: boolean;
  isLive?: boolean;
  subscriptionLabel?: ReactNode;
  upcomingShowLabel?: ReactNode;
  ageLimit?: number | null;
  isRounded?: boolean;
  // In seconds
  duration?: number;
  position?: number;
}

function getVariant(
  badges: {
    top: { left: unknown; right: unknown };
    bottom: { left: unknown; right: unknown };
    center: unknown;
  },
  isComplete: boolean
): Exclude<ThumbnailProps['variant'], undefined> {
  let variant: ThumbnailProps['variant'] = 'none';

  if (isComplete || badges.center) {
    return 'fill';
  }

  if (badges.top.left || badges.top.right) {
    variant = 'gradient-top';
  }

  if (badges.bottom.left || badges.bottom.right) {
    variant = variant === 'gradient-top' ? 'gradient-both' : 'gradient-bottom';
  }

  return variant;
}

function getRemainingTimeToWatchInMinutes(
  positionInSeconds: number,
  durationInSeconds: number
): number {
  return Math.ceil((durationInSeconds - positionInSeconds) / 60);
}

const getDefaultAssetBadge = ({
  isLive,
  isSponsored,
}: {
  isLive: boolean;
  isSponsored: boolean;
}) => {
  if (isLive) {
    return <SimpleLiveBadge />;
  }

  if (isSponsored) {
    return <AdBadge />;
  }

  return undefined;
};

export const AssetThumbnail: VFC<AssetThumbnailProps> = (props) => {
  const {
    imageSources,
    duration,
    className,
    badge,
    secondaryBadge,
    isLive = false,
    isSponsored = false,
    isComplete = false,
    subscriptionLabel,
    ageLimit,
    upcomingShowLabel,
    isRounded,
    position,
  } = props;

  const displayProgress = duration && position;

  const progress = displayProgress ? position / duration : undefined;

  const durationText = useMemo(() => {
    if (displayProgress) {
      return `${getRemainingTimeToWatchInMinutes(position, duration)} min`;
    }

    return duration ? formatDurationFromSeconds(duration) : undefined;
  }, [displayProgress, duration, position]);

  const ageLimitTag = useMemo(
    () =>
      ageLimit || ageLimit === 0 ? <AgeLimitLabel ageLimit={ageLimit} /> : null,
    [ageLimit]
  );

  const badges = useMemo(
    () => ({
      top: {
        left: badge || getDefaultAssetBadge({ isLive, isSponsored }),
        right: secondaryBadge || (isComplete && <CompleteIcon />),
      },
      bottom: {
        left:
          ageLimitTag || subscriptionLabel ? (
            <>
              {ageLimitTag}
              {subscriptionLabel}
            </>
          ) : undefined,
        right: !upcomingShowLabel && durationText,
      },
      center: upcomingShowLabel,
    }),
    [
      badge,
      secondaryBadge,
      isLive,
      isSponsored,
      isComplete,
      subscriptionLabel,
      durationText,
      upcomingShowLabel,
      ageLimitTag,
    ]
  );

  return (
    <Thumbnail
      imageSources={imageSources}
      className={className}
      isRounded={isRounded}
      progress={progress}
      variant={getVariant(badges, isComplete)}
    >
      <div>{badges.top.left}</div>
      <div>{badges.top.right}</div>
      <div>{badges.bottom.left}</div>
      <div>{badges.bottom.right}</div>
      <div>{badges.center}</div>
    </Thumbnail>
  );
};
